import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { services: SERVICES, cities: CITIES, lang } = useLang();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 6);

  return (
    <div data-testid={IDS.services.detail(service.slug)}>
      <Seo
        title={`${service.name} in New Brunswick | Maze Security`}
        description={service.summary}
        path={`/services/${service.slug}`}
      />
      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <Link
            to="/services"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-primary transition-colors mb-8 inline-block"
          >
            ← {lang === "fr" ? "Tous les services" : "All Services"}
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                [ {lang === "fr" ? "Service" : "Service"} · {String(SERVICES.indexOf(service) + 1).padStart(2, "0")} ]
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                className="font-serif font-light text-5xl md:text-8xl leading-[0.92] tracking-tighter"
              >
                {service.name.split(" ").slice(0, -1).join(" ")}{" "}
                <i className="text-primary">
                  {service.name.split(" ").slice(-1)[0]}
                </i>{" "}
                {lang === "fr" ? "au Nouveau-Brunswick." : "in New Brunswick."}
              </motion.h1>
            </div>
            <div className="md:col-span-4">
              <p className="text-foreground/80 leading-relaxed">
                {service.tagline}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Long form + bullets */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7 space-y-8">
            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/90 max-w-2xl">
              {service.summary}
            </p>
            {service.long.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-2xl"
              >
                {p}
              </p>
            ))}
          </div>
          <aside className="md:col-span-5">
            <div className="md:sticky md:top-32 border border-border p-8 md:p-10 bg-[hsl(0_0%_6%)]">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
                [ {lang === "fr" ? "Inclus" : "Included"} ]
              </div>
              <ul className="space-y-4">
                {service.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check
                      size={16}
                      strokeWidth={1.5}
                      className="text-primary mt-1 flex-shrink-0"
                    />
                    <span className="text-foreground/85 leading-relaxed">
                      {b}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-8 border-t border-border">
                <Link
                  to="/contact#quote"
                  className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
                  data-testid="service-detail-cta"
                >
                  {lang === "fr" ? "Demander un devis" : "Request Quote"} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Coverage cities */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-16 md:py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Available In ]
          </div>
          <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-10">
            {service.name} — <i className="text-primary">Across NB</i>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-y-4 gap-x-6">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/cities/${c.slug}`}
                className="font-serif text-lg md:text-xl hover:text-primary transition-colors border-b border-border py-2"
                data-testid={`service-detail-city-${c.slug}`}
              >
                {service.name} · {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO keywords semantic list */}
      <section className="border-b border-border bg-[hsl(0_0%_5%)]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-16">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Related Searches ]
          </div>
          <div className="flex flex-wrap gap-3">
            {service.keywords.map((k) => (
              <span
                key={k}
                className="font-mono text-[11px] tracking-widest uppercase border border-border px-4 py-2 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Other services */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Explore More ]
          </div>
          <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-10">
            Other <i className="text-primary">Disciplines</i>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="border border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
                data-testid={`related-service-${o.slug}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground">
                    {String(SERVICES.indexOf(o) + 1).padStart(2, "0")}
                  </span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.2}
                    className="group-hover:translate-x-1 group-hover:text-primary transition-all"
                  />
                </div>
                <h4 className="font-serif text-2xl leading-tight group-hover:text-primary transition-colors mb-2">
                  {o.name}
                </h4>
                <p className="font-mono text-[11px] text-muted-foreground tracking-wide">
                  {o.tagline}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <QuoteForm defaultService={service.name} />
    </div>
  );
}
